import { AppError, isPickerCancel, toAppError } from "./errors";
import { Emitter } from "./events";
import {
  canvasSizeFor,
  videoBitrateFor,
  type AppSettings,
  type Phase,
  type PipLayout,
  type QualitySettings,
  type ScreenEntry,
  type Take,
} from "./types";
import { Stage } from "../compose/stage";
import { captureCamera, captureDisplay, captureMicrophone, stopStream } from "../capture/devices";
import { AudioMixer, type AudioKind, type AudioLevels } from "../capture/mixer";
import { Encoder, negotiateFormat, type EncodeChoice } from "../encode/encoder";
import { Journal } from "../store/journal";
import {
  FileSink,
  folderPermission,
  getSaveFolder,
  timestampName,
  type FolderRef,
} from "../store/destination";
import { saveTake } from "../store/takes";

export type Notice = { message: string; kind: "info" | "ok" | "error" };

interface SessionEvents extends Record<string, unknown> {
  phase: Phase;
  sources: void;
  levels: AudioLevels;
  tick: number;
  saved: Take;
  failed: AppError;
  notice: Notice;
}

type StopReason = "user" | "interrupted" | "encoder" | "write" | "shutdown";

/** A disk call that never returns must not freeze the pipeline. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  const { promise: timed, reject } = Promise.withResolvers<T>();
  const timer = window.setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms);
  return Promise.race([promise, timed]).finally(() => window.clearTimeout(timer));
}

const WATCHDOG_MS = 2000;
const STALL_MS = 6000;

/**
 * The saved file's size on disk, or null when it is missing or shorter than
 * what was counted. At least the counted bytes must be there; a write that
 * timed out but landed leaves the file longer, and that is still good.
 */
async function survivedSize(
  folder: FolderRef | null,
  fileName: string,
  bytes: number,
): Promise<number | null> {
  if (!folder || bytes <= 0) return null;
  try {
    const handle = await folder.handle.getFileHandle(fileName);
    const file = await handle.getFile();
    return file.size >= bytes && file.size > 0 ? file.size : null;
  } catch {
    return null;
  }
}

/**
 * The whole recording lifecycle, owned in one place.
 *
 * Invariants this class exists to hold:
 *  - every exit path (user stop, dead track, encoder failure, write failure,
 *    page close) goes through `finalize`, exactly once;
 *  - the take is on disk from the first second, in the destination file and
 *    in a durable journal, so no path can lose more than the last second;
 *  - teardown always stops every track it started — no orphaned camera light.
 */
export class Session {
  readonly events = new Emitter<SessionEvents>();
  readonly stage: Stage;

  private settings: AppSettings;
  private screens: ScreenEntry[] = [];
  private cameraStream: MediaStream | null = null;
  private micStream: MediaStream | null = null;
  private mixer = new AudioMixer();
  private encoder: Encoder | null = null;
  private journal: Journal | null = null;
  private sink: FileSink | null = null;
  private folder: FolderRef | null = null;
  private choice: EncodeChoice | null = null;

  private phaseValue: Phase = "idle";
  private sessionId: string | null = null;
  private writeQueue: Promise<void> = Promise.resolve();
  private writeFailures = 0;
  private journalAppends = 0;
  private lastEncoderChunks = 0;
  private journalBytesWritten = 0;
  private journalLastError: string | null = null;
  /** False when the journal could not open; the file is then the only copy. */
  private journalUsable = true;
  private sinkWrites = 0;
  private sinkFailures = 0;
  private sinkOnlyFailures = 0;
  private lastSinkError: string | null = null;
  private finalizing: Promise<Take | null> | null = null;
  private watchdog = 0;
  private ticker = 0;
  private levelTicker = 0;
  private wakeLock: WakeLockSentinel | null = null;

  private accumulatedMs = 0;
  private segmentStart = 0;
  /** Size fixed at start: changing quality mid-take must not rewrite history. */
  private takeSize: { width: number; height: number } | null = null;
  /** Sources we are stopping on purpose; their `ended` event is not a failure. */
  private stoppingIntentionally = new Set<string>();
  private unloadHandler: (event: BeforeUnloadEvent) => void = () => {};

  constructor(canvas: HTMLCanvasElement, settings: AppSettings) {
    this.settings = settings;
    this.stage = new Stage(canvas, settings.pip, settings.quality.fps);
    const size = canvasSizeFor(settings.quality.height);
    this.stage.setSize(size.width, size.height);
  }

  get phase(): Phase {
    return this.phaseValue;
  }

  get isActive(): boolean {
    return (
      this.phaseValue === "recording" ||
      this.phaseValue === "paused" ||
      this.phaseValue === "arming"
    );
  }

  get isRecording(): boolean {
    return this.phaseValue === "recording" || this.phaseValue === "paused";
  }

  get screenEntries(): ScreenEntry[] {
    return [...this.screens];
  }

  get hasCamera(): boolean {
    return this.cameraStream !== null;
  }

  get hasSystemAudio(): boolean {
    return this.screens.some((screen) => screen.hasSystemAudio);
  }

  get elapsedMs(): number {
    if (this.phaseValue === "recording" && this.segmentStart > 0) {
      return this.accumulatedMs + (Date.now() - this.segmentStart);
    }
    return this.accumulatedMs;
  }

  /** The folder this session is writing to, for surfaces that save files. */
  get saveFolder(): FolderRef | null {
    return this.folder;
  }

  get formatLabel(): string {
    if (!this.choice) return "MP4 is not available in this browser — use Chrome 126 or newer";
    const container = this.choice.extension.toUpperCase();
    return this.choice.native
      ? `${container} · H.264 + AAC · plays in QuickTime`
      : `${container} · ${this.choice.mime.includes("vp9") ? "VP9" : "VP8"} + Opus · this browser could not encode MP4`;
  }

  async init(): Promise<void> {
    this.journal = new Journal();
    if (!this.folder) {
      try {
        this.folder = await getSaveFolder();
      } catch {
        this.folder = null;
      }
    }
    try {
      this.choice = negotiateFormat();
    } catch {
      this.choice = null;
    }
    if (this.folder) {
      const state = await folderPermission(this.folder);
      if (state !== "granted") {
        this.events.emit("notice", {
          message: "The save folder needs permission again before recording.",
          kind: "info",
        });
      }
    }
  }

  // ---------------------------------------------------------------- sources

  async addScreen(): Promise<ScreenEntry | null> {
    let capture;
    try {
      capture = await captureDisplay({
        fps: this.settings.quality.fps,
        wantSystemAudio: this.settings.systemAudioOn,
      });
    } catch (error) {
      if (error instanceof AppError && error.code === "interrupted") return null;
      throw toAppError(error, "permission");
    }
    return this.attachScreenStream(capture.stream, capture.label, capture.hasSystemAudio);
  }

  /**
   * Attaches a stream that is already captured. The picker path uses it, and
   * so does an embedder (the portal) that brings its own tracks.
   */
  attachScreenStream(
    stream: MediaStream,
    label: string,
    hasSystemAudio = false,
  ): ScreenEntry {
    const named = /^(Screen|Window|Tab)$/.test(label)
      ? `${label} ${this.screens.length + 1}`
      : label;
    const entry: ScreenEntry = {
      id: crypto.randomUUID(),
      label: named,
      stream,
      hasSystemAudio,
    };
    const videoTrack = stream.getVideoTracks()[0];
    videoTrack?.addEventListener("ended", () => {
      if (this.stoppingIntentionally.delete(entry.id)) {
        this.removeScreen(entry.id);
        return;
      }
      this.events.emit("notice", { message: "The screen share ended.", kind: "info" });
      this.dropScreen(entry.id);
    });
    this.screens.push(entry);
    this.stage.addScreen(entry.id, entry.label, entry.stream, entry.hasSystemAudio);
    if (this.isRecording && hasSystemAudio && !this.mixer.has("system")) {
      void this.mixer
        .attach("system", stream)
        .catch((error: unknown) => console.error("system audio could not be added", error));
    }
    this.afterSourcesChanged();
    return entry;
  }

  /**
   * Screens can be added and removed mid-take: the compositor re-lays out and
   * the recording keeps going. Removing the last source is what ends it.
   */
  removeScreen(id: string): void {
    const index = this.screens.findIndex((screen) => screen.id === id);
    if (index < 0) return;
    const [entry] = this.screens.splice(index, 1);
    if (entry) {
      const track = entry.stream.getVideoTracks()[0];
      if (track && track.readyState === "live") this.stoppingIntentionally.add(id);
      stopStream(entry.stream);
    }
    this.stage.removeScreen(id);
    this.releaseSystemAudioIfUnused();
    this.afterSourcesChanged();
    this.endTakeIfNothingLeft();
  }

  /** Drops a screen whose track died on its own, without ending the take. */
  private dropScreen(id: string): void {
    const index = this.screens.findIndex((screen) => screen.id === id);
    if (index < 0) return;
    this.screens.splice(index, 1);
    this.stage.removeScreen(id);
    this.releaseSystemAudioIfUnused();
    this.afterSourcesChanged();
    this.endTakeIfNothingLeft();
  }

  private releaseSystemAudioIfUnused(): void {
    const survivor = this.screens.find((screen) => screen.hasSystemAudio);
    if (!survivor) {
      if (!this.mixer.has("system")) return;
      this.mixer.detach("system");
      if (!this.mixer.has("mic")) this.stopLevelMeter();
      return;
    }
    // The mixer still holds the removed screen's track; the survivor's audio
    // was never attached because the slot was taken. Swap it in.
    const held = this.mixer.streamFor("system");
    if (held && held.id === survivor.stream.id) return;
    void this.mixer
      .attach("system", survivor.stream)
      .catch((error: unknown) => console.error("system audio could not be moved", error));
  }

  private endTakeIfNothingLeft(): void {
    if (!this.isRecording) return;
    if (this.screens.length > 0 || this.cameraStream) return;
    this.events.emit("notice", {
      message: "Every source is gone — saving the take.",
      kind: "info",
    });
    void this.finalize("interrupted");
  }

  async setCamera(on: boolean): Promise<void> {
    if (on) {
      if (this.cameraStream) return;
      this.attachCameraStream(await captureCamera());
    } else {
      this.detachCamera();
    }
    this.afterSourcesChanged();
  }

  attachCameraStream(stream: MediaStream): void {
    const track = stream.getVideoTracks()[0];
    track?.addEventListener("ended", () => {
      if (track && this.stoppingIntentionally.delete(track.id)) return;
      this.cameraStream = null;
      this.stage.setCamera(null);
      this.events.emit("notice", { message: "The camera stopped.", kind: "info" });
      this.events.emit("sources", undefined);
      this.endTakeIfNothingLeft();
    });
    this.cameraStream = stream;
    // A camera track can report its size late (virtual cameras, first frames).
    // Resize once it settles, so a camera-only take is not stuck at 16:9.
    if (track) {
      const settle = () => {
        if (!this.isRecording) this.applyOutputSize();
      };
      track.addEventListener("unmute", settle, { once: true });
      window.setTimeout(settle, 600);
    }
    this.stage.setCamera(stream);
    this.events.emit("sources", undefined);
  }

  detachCamera(): void {
    const stream = this.cameraStream;
    this.cameraStream = null;
    this.stage.setCamera(null);
    if (stream) {
      const track = stream.getVideoTracks()[0];
      if (track) this.stoppingIntentionally.add(track.id);
      stopStream(stream);
    }
    this.events.emit("sources", undefined);
    this.endTakeIfNothingLeft();
  }

  async setMic(on: boolean): Promise<void> {
    if (on) {
      if (this.micStream) return;
      await this.attachMicStream(await captureMicrophone());
    } else {
      this.detachMic();
    }
    this.events.emit("sources", undefined);
  }

  async attachMicStream(stream: MediaStream): Promise<void> {
    this.micStream = stream;
    await this.mixer.attach("mic", stream);
    this.startLevelMeter();
    this.events.emit("sources", undefined);
  }

  detachMic(): void {
    const stream = this.micStream;
    this.micStream = null;
    this.mixer.detach("mic");
    stopStream(stream);
    if (!this.mixer.has("system")) this.stopLevelMeter();
    this.events.emit("sources", undefined);
  }

  /** Lets an embedder (or a test) provide the destination folder directly. */
  useFolder(folder: FolderRef | null): void {
    this.folder = folder;
  }

  /** Applies to screens added from now on; Chrome captures audio at pick time. */
  setSystemAudio(on: boolean): void {
    this.settings.systemAudioOn = on;
    if (on && this.screens.length > 0 && !this.hasSystemAudio) {
      this.events.emit("notice", {
        message: "System audio is captured when a screen is added — add the screen again to include it.",
        kind: "info",
      });
    }
  }

  /**
   * Records only part of a screen. Works mid-take too: the compositor starts
   * cutting to the new area on the next frame. The output frame keeps the
   * shape it started with, because an encoder cannot change dimensions
   * mid-file — so a region with a different aspect is letterboxed until the
   * next take, which sizes itself to the region.
   */
  setScreenCrop(id: string, crop: import("../compose/layout").CropRect | null): void {
    this.stage.setCrop(id, crop);
    if (!this.isRecording) this.applyOutputSize();
    this.events.emit("sources", undefined);
  }

  /**
   * Output frame size. One cropped screen sizes the frame to that region's
   * aspect, so a region recording is the region — not a strip in a black
   * frame. Several screens keep the 16:9 grid.
   */
  private computeOutputSize(): { width: number; height: number } {
    const base = canvasSizeFor(this.settings.quality.height);
    if (this.screens.length === 0 && this.cameraStream) {
      // Camera only: the frame takes the camera's shape, so no bars and no
      // cropping of a self-recording.
      const settings = this.cameraStream.getVideoTracks()[0]?.getSettings();
      const aspect = (settings?.width ?? 16) / (settings?.height ?? 9);
      const height = Math.max(2, Math.round(base.height / 2) * 2);
      const width = Math.max(2, Math.round((height * aspect) / 2) * 2);
      return { width, height };
    }
    const only = this.screens.length === 1 ? this.screens[0] : undefined;
    if (!only) return base;
    const crop = this.stage.getCrop(only.id);
    if (!crop) return base;
    const settings = only.stream.getVideoTracks()[0]?.getSettings();
    const sourceWidth = (settings?.width ?? 0) * crop.w;
    const sourceHeight = (settings?.height ?? 0) * crop.h;
    if (sourceWidth <= 0 || sourceHeight <= 0) return base;
    const height = Math.max(2, Math.round(base.height / 2) * 2);
    const width = Math.max(2, Math.round((height * (sourceWidth / sourceHeight)) / 2) * 2);
    return { width, height };
  }

  private applyOutputSize(): void {
    if (this.isRecording) return;
    const size = this.computeOutputSize();
    this.stage.setSize(size.width, size.height);
  }

  setPip(pip: PipLayout): void {
    this.settings.pip = pip;
    this.stage.setPip(pip);
  }

  setQuality(quality: QualitySettings): void {
    if (this.isRecording) {
      this.events.emit("notice", { message: "Quality applies to the next take.", kind: "info" });
      return;
    }
    this.settings.quality = quality;
    this.applyOutputSize();
    this.stage.setFps(quality.fps);
  }

  private afterSourcesChanged(): void {
    if (!this.isRecording) this.applyOutputSize();
    this.events.emit("sources", undefined);
    if (this.stage.screenCount > 0 || this.stage.cameraOn) this.stage.start();
    else this.stage.stop();
    this.stage.draw();
  }

  // -------------------------------------------------------------- recording

  async start(): Promise<void> {
    if (this.finalizing) await this.finalizing;
    if (this.isActive) return;
    if (this.screens.length === 0 && !this.cameraStream) {
      throw new AppError("no-source", "Add a screen or turn the camera on first.");
    }

    this.setPhase("arming");
    this.accumulatedMs = 0;
    this.segmentStart = 0;

    if (this.settings.countdownSeconds > 0) {
      await this.countdown(this.settings.countdownSeconds);
    }
    if (this.phaseValue !== "arming") return;

    try {
      await this.beginCapture();
    } catch (error) {
      await this.abortCapture();
      await this.stopCaptureResources();
      this.setPhase("failed");
      throw toAppError(error, "internal");
    }
  }

  /** Closes anything a half-started take opened, without touching sources. */
  private async abortCapture(): Promise<void> {
    const encoder = this.encoder;
    this.encoder = null;
    try {
      await encoder?.stop();
    } catch {
      // Nothing was recording.
    }
    const sink = this.sink;
    this.sink = null;
    try {
      await sink?.abort();
    } catch {
      // Bytes already flushed stay.
    }
    const sessionId = this.sessionId;
    this.sessionId = null;
    if (sessionId && this.journal) {
      try {
        await this.journal.close(sessionId);
        await this.journal.remove(sessionId);
      } catch {
        // Best effort.
      }
    }
  }

  private async countdown(seconds: number): Promise<void> {
    for (let remaining = seconds; remaining > 0; remaining--) {
      this.events.emit("tick", remaining);
      await new Promise<void>((resolve) => setTimeout(resolve, 1000));
      if (this.phaseValue !== "arming") return;
    }
  }

  private async beginCapture(): Promise<void> {
    const folder = this.folder ?? (await getSaveFolder());
    if (!folder) throw new AppError("no-folder", "Choose a folder to save recordings into.");
    const permission = await folderPermission(folder);
    if (permission !== "granted") {
      throw new AppError(
        "no-folder",
        "The save folder needs permission again — open Settings and choose it once more.",
      );
    }
    this.folder = folder;

    const choice = this.choice ?? negotiateFormat();
    this.choice = choice;
    const size = this.computeOutputSize();
    this.stage.setSize(size.width, size.height);
    this.stage.setFps(this.settings.quality.fps);
    this.stage.setPip(this.settings.pip);
    this.stage.start();
    this.stage.draw();

    const sessionId = crypto.randomUUID();
    this.sessionId = sessionId;
    this.takeSize = { width: size.width, height: size.height };
    const fileName = timestampName(new Date(), choice.extension);

    const sink = await FileSink.open(folder.handle, fileName);
    if (this.phaseValue !== "arming") {
      await sink.abort();
      this.sessionId = null;
      return;
    }
    this.sink = sink;
    this.writeFailures = 0;
    this.writeQueue = Promise.resolve();

    if (!this.journal) this.journal = new Journal();
    // A journal that cannot open must not stop the recording: the file is the
    // primary copy, the journal is the crash net. Say so once and carry on.
    this.journalUsable = true;
    try {
      await withTimeout(this.journal.open(sessionId, choice.extension), 10_000);
    } catch (error) {
      this.journalUsable = false;
      this.journalLastError = error instanceof Error ? error.message : String(error);
      this.events.emit("notice", {
        message: "The recovery journal is unavailable — this take is written to your folder only.",
        kind: "error",
      });
      console.error("journal open failed", error);
    }
    if (this.phaseValue !== "arming") {
      await sink.abort();
      try {
        await this.journal.close(sessionId);
        await this.journal.remove(sessionId);
      } catch {
        // Best effort.
      }
      this.sink = null;
      this.sessionId = null;
      return;
    }

    if (this.micStream) {
      try {
        await this.mixer.attach("mic", this.micStream);
      } catch (error) {
        this.events.emit("notice", { message: "Microphone could not be added.", kind: "error" });
        console.error(error);
      }
    }
    const systemTrack = this.screens.find((screen) => screen.hasSystemAudio);
    if (systemTrack) {
      try {
        await this.mixer.attach("system", systemTrack.stream);
      } catch (error) {
        this.events.emit("notice", { message: "System audio could not be added.", kind: "error" });
        console.error(error);
      }
    }

    const stream = this.stage.canvas.captureStream(this.settings.quality.fps);
    // The destination exists from the first frame, silent until a source
    // arrives, so a microphone switched on mid-take still reaches the file.
    const audioTrack = (await this.mixer.ensureOutput()).getAudioTracks()[0];
    if (audioTrack) stream.addTrack(audioTrack);

    const encoder = new Encoder();
    this.encoder = encoder;
    encoder.start(
      stream,
      choice,
      {
        videoBitsPerSecond: videoBitrateFor(size.width, size.height, this.settings.quality.fps),
        audioBitsPerSecond: 160_000,
      },
      (chunk) => this.queueChunk(sessionId, chunk),
      (error) => {
        this.events.emit("notice", { message: error.message, kind: "error" });
        void this.finalize("encoder");
      },
    );

    this.segmentStart = Date.now();
    this.setPhase("recording");
    this.startTicker();
    this.startWatchdog();
    this.startLevelMeter();
    void this.requestWakeLock();
    this.installUnloadGuard();
  }

  pause(): void {
    if (this.phaseValue !== "recording") return;
    this.encoder?.pause();
    if (this.segmentStart > 0) {
      this.accumulatedMs += Date.now() - this.segmentStart;
      this.segmentStart = 0;
    }
    this.setPhase("paused");
  }

  resume(): void {
    if (this.phaseValue !== "paused") return;
    this.encoder?.resume();
    this.segmentStart = Date.now();
    this.setPhase("recording");
  }

  setMicMuted(muted: boolean): void {
    this.mixer.setMuted("mic", muted);
  }

  get micMuted(): boolean {
    return this.mixer.isMuted("mic");
  }

  /** Hides the camera live: the box leaves the frame, the light goes out. */
  hideCameraLive(hidden: boolean): void {
    this.stage.setCamera(hidden ? null : this.cameraStream);
  }

  get cameraHiddenLive(): boolean {
    return this.cameraStream !== null && !this.stage.cameraOn;
  }

  async stop(): Promise<Take | null> {
    return this.finalize("user");
  }

  private queueChunk(sessionId: string, chunk: Blob): void {
    // Bound to the session that produced it: a chunk from a finished take can
    // never land in the next take's file.
    if (this.sessionId !== sessionId) return;
    this.writeQueue = this.writeQueue.then(async () => {
      if (this.sessionId !== sessionId) return;
      const sink = this.sink;
      const journal = this.journal;
      const writes: Promise<unknown>[] = [];
      let journalOk = false;
      let sinkOk = false;

      if (sink) {
        writes.push(
          withTimeout(sink.write(chunk), 10_000).then(
            () => {
              this.sinkWrites += 1;
              sinkOk = true;
            },
            (error: unknown) => {
              this.sinkFailures += 1;
              this.lastSinkError = error instanceof Error ? error.message : String(error);
              console.error("file append failed", error);
            },
          ),
        );
      }
      if (journal) {
        writes.push(
          withTimeout(journal.append(sessionId, chunk), 10_000).then(
            (bytes) => {
              this.journalAppends += 1;
              this.journalBytesWritten = bytes as number;
              journalOk = true;
            },
            (error: unknown) => {
              this.journalLastError = error instanceof Error ? error.message : String(error);
              console.error("journal append failed", error);
            },
          ),
        );
      }

      // Written in parallel: a slow journal must never hold back the file.
      await Promise.all(writes);
      if (!sinkOk && journalOk) {
        // The folder is failing but the journal is not: the take is safe, the
        // user just believes it is landing in their folder.
        this.sinkOnlyFailures += 1;
        if (this.sinkOnlyFailures === 3) {
          this.events.emit("notice", {
            message: `The folder stopped accepting writes (${this.lastSinkError ?? "unknown"}). The take is still being written to the recovery journal.`,
            kind: "error",
          });
        }
      }
      if (!journalOk && !sinkOk) {
        this.writeFailures += 1;
        if (this.writeFailures >= 3) void this.finalize("write");
      }
    });
  }

  /**
   * Single exit path. Idempotent: whichever trigger gets here first wins and
   * every other caller awaits the same promise.
   */
  finalize(reason: StopReason): Promise<Take | null> {
    if (this.finalizing) return this.finalizing;
    const run = this.runFinalize(reason);
    this.finalizing = run;
    void run.finally(() => {
      this.finalizing = null;
    });
    return run;
  }

  private async runFinalize(reason: StopReason): Promise<Take | null> {
    const wasActive = this.isRecording || this.phaseValue === "arming";
    if (!wasActive && !this.encoder && !this.sink) return null;

    // Cancelled during the countdown: nothing was ever opened.
    if (!this.encoder && !this.sink) {
      await this.stopCaptureResources();
      this.setPhase("idle");
      return null;
    }

    this.stopTicker();
    this.stopWatchdog();
    this.stopLevelMeter();

    const durationMs = this.elapsedMs;
    if (this.segmentStart > 0) {
      this.accumulatedMs += Date.now() - this.segmentStart;
      this.segmentStart = 0;
    }
    const sessionId = this.sessionId;
    this.setPhase("finalizing");

    const encoder = this.encoder;
    this.encoder = null;
    this.lastEncoderChunks = encoder?.chunkCount ?? 0;
    try {
      await encoder?.stop();
    } catch (error) {
      console.error("encoder stop failed", error);
    }

    // Every queued chunk must land before the file is closed.
    try {
      await this.writeQueue;
    } catch {
      // Individual failures were already counted.
    }

    let take: Take | null = null;

    // The destination file is committed FIRST and on its own: a journal
    // failure must never leave the writable unclosed and the file unplayable.
    const sink = this.sink;
    this.sink = null;
    let sinkBytes = 0;
    if (sink) {
      try {
        sinkBytes = await withTimeout(sink.close(), 20_000);
      } catch (error) {
        console.error("file close failed", error);
        try {
          await sink.abort();
        } catch {
          // The bytes already flushed stay.
        }
      }
    }

    let journalBytes = 0;
    let journalCloseFailed = false;
    if (sessionId && this.journal && this.journalUsable) {
      try {
        journalBytes = await withTimeout(this.journal.close(sessionId), 10_000);
      } catch (error) {
        // "Unknown" must not read as "empty": deleting the journal here would
        // destroy the only complete copy of a take the file is missing.
        journalCloseFailed = true;
        console.error("journal close failed", error);
      }
    }

    try {
      if (sink && sessionId && journalBytes > sinkBytes) {
        // The destination lagged the journal (crash-adjacent close, buffering):
        // rewrite it from the durable copy so the file is complete.
        try {
          const copy = await this.journal?.read(sessionId);
          if (copy) await sink.repairFrom(copy.blob);
        } catch (error) {
          console.error("repair from journal failed", error);
        }
      }

      const counted = Math.max(journalBytes, sinkBytes);
      // A folder can disappear under an open handle: OPFS then accepts writes
      // into the orphaned file and the take is reported saved with nothing on
      // disk. Look for the file before trusting the write, and take its size
      // as the truth — a write that timed out may have landed uncounted.
      const onDisk = sink && !journalCloseFailed ? await survivedSize(this.folder, sink.fileName, counted) : null;
      const intact = onDisk !== null;
      const bytes = onDisk ?? counted;
      if (sink && intact && bytes > 0 && this.folder) {
        const size = this.takeSize ?? canvasSizeFor(this.settings.quality.height);
        take = {
          id: sessionId ?? crypto.randomUUID(),
          name: sink.fileName.replace(/\.[^.]+$/, ""),
          fileName: sink.fileName,
          mime: this.choice?.mime ?? "video/mp4",
          bytes,
          durationMs,
          width: size.width,
          height: size.height,
          createdAt: Date.now(),
          thumb: this.stage.snapshot(),
          folderName: this.folder.name,
          fileHandle: sink.fileHandle,
          recovered: false,
        };
        await saveTake(take);
        if (sessionId && !journalCloseFailed) await this.journal?.remove(sessionId);
      } else if (sessionId) {
        this.events.emit("notice", {
          message: journalCloseFailed
            ? "The journal could not confirm the take — recover it from the journal."
            : sink && !intact
              ? "The folder lost the file — the take stays in the recovery journal."
              : "Nothing was written to disk — the take stays in the recovery journal.",
          kind: "error",
        });
      }
    } catch (error) {
      const appError = toAppError(error, "write");
      this.events.emit("failed", appError);
      this.events.emit("notice", { message: appError.message, kind: "error" });
    }

    this.removeUnloadGuard();
    await this.stopCaptureResources();
    this.sessionId = null;
    this.takeSize = null;

    if (take) {
      this.setPhase("saved");
      this.events.emit("saved", take);
    } else {
      this.setPhase("failed");
      if (reason === "interrupted") {
        this.events.emit("notice", {
          message: "The capture ended early. What was recorded is saved.",
          kind: "info",
        });
      }
    }
    return take;
  }

  /**
   * Ends what the take owned — timers, wake lock, unload guard — and leaves
   * the sources running: the preview stays live and the next take does not
   * need the pickers again.
   */
  private async stopCaptureResources(): Promise<void> {
    this.stopTicker();
    this.stopWatchdog();
    this.stopLevelMeter();
    this.removeUnloadGuard();
    if (this.wakeLock) {
      try {
        await this.wakeLock.release();
      } catch {
        // Already released.
      }
      this.wakeLock = null;
    }
    if (this.stage.screenCount > 0 || this.stage.cameraOn) this.stage.start();
  }

  /** Full release: every device, every stream, the audio graph. */
  async teardown(): Promise<void> {
    await this.stopCaptureResources();
    await this.stage.release();
    await this.mixer.close();
    this.mixer = new AudioMixer();
    for (const screen of this.screens) stopStream(screen.stream);
    this.screens = [];
    this.stage.setCamera(null);
    stopStream(this.cameraStream);
    this.cameraStream = null;
    stopStream(this.micStream);
    this.micStream = null;
    this.events.emit("sources", undefined);
  }

  // --------------------------------------------------------------- recovery

  /** Counters for the self-test and for the portal's own telemetry. */
  get debug(): {
    journalAppends: number;
    journalBytesWritten: number;
    journalLastError: string | null;
    sinkWrites: number;
    writeFailures: number;
    encoderChunks: number;
  } {
    return {
      journalAppends: this.journalAppends,
      journalBytesWritten: this.journalBytesWritten,
      journalLastError: this.journalLastError,
      sinkWrites: this.sinkWrites,
      writeFailures: this.writeFailures,
      encoderChunks: this.encoder?.chunkCount ?? this.lastEncoderChunks,
    };
  }

  /** Raw journal entries, including empty ones (diagnostics and cleanup). */
  async journalEntries(): Promise<{ session: string; extension: "mp4" | "webm"; bytes: number }[]> {
    if (!this.journal) this.journal = new Journal();
    return this.journal.list();
  }

  async listUnfinished(): Promise<{ session: string; extension: "mp4" | "webm"; bytes: number }[]> {
    const entries = await this.journalEntries();
    return entries.filter((entry) => entry.bytes > 0);
  }

  /** Turns an interrupted take from the journal into a real file. */
  async recover(session: string, extension: "mp4" | "webm" = "mp4"): Promise<Take | null> {
    const journal = this.journal ?? new Journal();
    this.journal = journal;
    const copy = await journal.read(session);
    const blob = copy.blob;
    if (blob.size === 0) {
      await journal.remove(session);
      return null;
    }
    const ext = copy.extension ?? extension;
    const folder = this.folder ?? (await getSaveFolder());
    if (!folder) throw new AppError("no-folder", "Choose a folder to save the recovered take.");
    const permission = await folderPermission(folder);
    if (permission !== "granted") {
      throw new AppError("no-folder", "The save folder needs permission again.");
    }
    this.folder = folder;

    const fileName = timestampName(new Date(), ext).replace(`.${ext}`, ` (recovered).${ext}`);
    const sink = await FileSink.open(folder.handle, fileName);
    await sink.write(blob);
    await sink.close();

    const size = this.takeSize ?? canvasSizeFor(this.settings.quality.height);
    const take: Take = {
      id: session,
      name: fileName.replace(/\.[^.]+$/, ""),
      fileName,
      mime: ext === "webm" ? "video/webm" : "video/mp4",
      bytes: blob.size,
      durationMs: 0,
      width: size.width,
      height: size.height,
      createdAt: Date.now(),
      thumb: null,
      folderName: folder.name,
      fileHandle: sink.fileHandle,
      recovered: true,
    };
    await saveTake(take);
    await journal.remove(session);
    this.events.emit("saved", take);
    return take;
  }

  async discardJournal(session: string): Promise<void> {
    if (!this.journal) this.journal = new Journal();
    await this.journal.remove(session);
  }

  // ----------------------------------------------------------------- timers

  private startTicker(): void {
    this.stopTicker();
    this.ticker = window.setInterval(() => {
      this.events.emit("tick", this.elapsedMs);
    }, 250);
  }

  private stopTicker(): void {
    if (this.ticker) window.clearInterval(this.ticker);
    this.ticker = 0;
  }

  private startLevelMeter(): void {
    this.stopLevelMeter();
    this.levelTicker = window.setInterval(() => {
      this.events.emit("levels", this.mixer.levels());
    }, 100);
  }

  private stopLevelMeter(): void {
    if (this.levelTicker) window.clearInterval(this.levelTicker);
    this.levelTicker = 0;
    this.events.emit("levels", { mic: 0, system: 0 });
  }

  private startWatchdog(): void {
    this.stopWatchdog();
    this.watchdog = window.setInterval(() => {
      const encoder = this.encoder;
      if (!encoder) return;
      if (encoder.error) {
        void this.finalize("encoder");
        return;
      }
      if (this.phaseValue === "recording" && encoder.msSinceChunk > STALL_MS) {
        this.events.emit("notice", {
          message: "The encoder stalled — saving what we have.",
          kind: "error",
        });
        void this.finalize("encoder");
      }
    }, WATCHDOG_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdog) window.clearInterval(this.watchdog);
    this.watchdog = 0;
  }

  private async requestWakeLock(): Promise<void> {
    try {
      if (!navigator.wakeLock) return;
      const sentinel = await navigator.wakeLock.request("screen");
      this.wakeLock = sentinel;
      sentinel.addEventListener("release", () => {
        this.wakeLock = null;
      });
    } catch {
      // Not fatal: the screen may sleep, the recording continues.
    }
  }

  private installUnloadGuard(): void {
    this.removeUnloadGuard();
    this.unloadHandler = (event: BeforeUnloadEvent) => {
      // Saving counts: the take is not committed until saveTake returns.
      if (!this.isRecording && this.phaseValue !== "finalizing") return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", this.unloadHandler);
  }

  private removeUnloadGuard(): void {
    window.removeEventListener("beforeunload", this.unloadHandler);
  }

  private setPhase(phase: Phase): void {
    this.phaseValue = phase;
    this.events.emit("phase", phase);
  }

  async dispose(): Promise<void> {
    if (this.finalizing) await this.finalizing;
    if (this.isRecording || this.phaseValue === "arming") await this.finalize("shutdown");
    await this.teardown();
    this.journal?.terminate();
    this.journal = null;
  }
}

export { isPickerCancel };
export type { AudioKind };
